import { createContext, useContext, ReactNode } from 'react';
import {
  useFeatureFlagsQuery,
  FeatureFlagsType,
} from '@/domains/shared/api/queries/useFeatureFlagsQuery';
import { CaptionM } from '@pensionbee/honeypot-ui';

type FeatureFlagsContextValue = {
  featureFlags: FeatureFlagsType | undefined;
  isLoading: boolean;
  error: Error | null;
  hasFlag: (flagName: string) => boolean;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

export const useFeatureFlags = () => {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return ctx;
};

type FeatureFlagWrapperProps = {
  /** The name of the flag to check */
  flag: string;
  /** Content to render when the feature is enabled */
  children: ReactNode;
  /** Content to render when the feature is disabled (default: null) */
  fallback?: ReactNode;
};

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const { data: featureFlags, isLoading, error } = useFeatureFlagsQuery();

  const hasFlag = (flagName: string): boolean => {
    return featureFlags?.[flagName] === true;
  };

  return (
    <FeatureFlagsContext.Provider
      value={{
        featureFlags,
        isLoading,
        error: error instanceof Error ? error : null,
        hasFlag,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const PreviewFeatureIndicator = () => (
  <div className="flex justify-end">
    <CaptionM className="rounded-full bg-yellow px-12 py-4">
      Preview feature
    </CaptionM>
  </div>
);

/**
 * Conditionally renders children based on a feature flag.
 *
 * @example
 * ```tsx
 * <FeatureFlagWrapper flag="honeyMaker">
 *   <NewFeature />
 * </FeatureFlagWrapper>
 * ```
 *
 * @example using the hook directly:
 * ```tsx
 * import { useFeatureFlags } from '@/domains/shared/components/AccountAccess/FeatureFlagsContext';
 *
 * const { hasFlag, featureFlags } = useFeatureFlags();
 * if (hasFlag('honeyMaker')) { ... }
 * ```
 */
export const FeatureFlagWrapper = ({
  flag,
  children,
  fallback = null,
}: FeatureFlagWrapperProps) => {
  const { hasFlag, isLoading, error } = useFeatureFlags();

  if (isLoading) {
    return null;
  }
  if (error) return <>{fallback}</>;

  if (hasFlag(flag)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};
